import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL;
const APPROVE_QR_URL = `${API_URL}/Custom/ApproveStudentEExamQr`;

export default function QrScannerModal({ show, onClose, examContext, onApproved }) {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const intervalRef = useRef(null);
    const approvingRef = useRef(false);
    const [manualToken, setManualToken] = useState('');
    const [status, setStatus] = useState('Kamera hazırlanıyor...');

    const stopCamera = useCallback(() => {
        if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
    }, []);

    const approveToken = useCallback(async (token) => {
        const normalizedToken = (token || '').trim();
        if (!normalizedToken || approvingRef.current) return;

        approvingRef.current = true;
        try {
            const { data } = await axios.post(APPROVE_QR_URL, {
                token: normalizedToken,
                examId: examContext?.examId,
                timeSlotId: examContext?.slotId,
                classroomId: examContext?.classroomId,
            });
            Swal.fire('QR Onaylandı', data?.message || 'Öğrencinin sınav oturumu açıldı.', 'success');
            onApproved?.();
            onClose?.();
        } catch (error) {
            const message = error?.response?.data || 'QR kod doğrulanamadı.';
            Swal.fire('Uyarı', String(message), 'warning');
        } finally {
            approvingRef.current = false;
        }
    }, [examContext?.classroomId, examContext?.examId, examContext?.slotId, onApproved, onClose]);

    useEffect(() => {
        if (!show) {
            stopCamera();
            return;
        }

        let cancelled = false;

        const startCamera = async () => {
            if (!navigator.mediaDevices?.getUserMedia) {
                setStatus('Bu tarayıcı kamera erişimini desteklemiyor. QR kod değerini elle girebilirsiniz.');
                return;
            }

            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: { ideal: 'environment' } },
                    audio: false,
                });

                if (cancelled) {
                    stream.getTracks().forEach(track => track.stop());
                    return;
                }

                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                }

                if (!('BarcodeDetector' in window)) {
                    setStatus('Kamera açık. Tarayıcınız otomatik QR okuma desteği sunmuyorsa kodu elle girebilirsiniz.');
                    return;
                }

                const Detector = window.BarcodeDetector;
                const supportedFormats = Detector.getSupportedFormats
                    ? await Detector.getSupportedFormats()
                    : ['qr_code'];

                if (!supportedFormats.includes('qr_code')) {
                    setStatus('Kamera açık. Bu tarayıcı QR kod algılamayı desteklemiyor; kodu elle girebilirsiniz.');
                    return;
                }

                const detector = new Detector({ formats: ['qr_code'] });
                setStatus('QR kodu kameraya gösteriniz.');

                intervalRef.current = window.setInterval(async () => {
                    if (!videoRef.current || approvingRef.current) return;
                    try {
                        const codes = await detector.detect(videoRef.current);
                        const rawValue = codes?.[0]?.rawValue;
                        if (rawValue) {
                            await approveToken(rawValue);
                        }
                    } catch {
                    }
                }, 650);
            } catch {
                setStatus('Kamera açılamadı. Tarayıcı izinlerini kontrol ediniz veya kodu elle giriniz.');
            }
        };

        startCamera();

        return () => {
            cancelled = true;
            stopCamera();
        };
    }, [approveToken, show, stopCamera]);

    if (!show) return null;

    return (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-75 d-print-none d-flex align-items-center justify-content-center" style={{ zIndex: 1060 }}>
            <div className="bg-white rounded p-4 shadow" style={{ width: 'min(560px, 94vw)' }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h5 className="mb-1">QR Oku</h5>
                        <div className="text-muted small">{examContext?.examName}</div>
                    </div>
                    <button type="button" className="btn btn-sm btn-light" onClick={onClose}>Kapat</button>
                </div>

                <div className="ratio ratio-4x3 bg-light rounded overflow-hidden mb-3">
                    <video ref={videoRef} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>

                <div className="alert alert-info py-2">{status}</div>

                <div className="input-group">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="QR kod değeri"
                        value={manualToken}
                        onChange={event => setManualToken(event.target.value)}
                    />
                    <button type="button" className="btn btn-primary" onClick={() => approveToken(manualToken)}>
                        Onayla
                    </button>
                </div>
            </div>
        </div>
    );
}
